import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, timer } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { AgentConversationApiService } from '../../services/agent-conversation-api.service';
import { AgentConversationListItem, AgentConversationFilter, conversationStatusLabel } from '../../models/agent-conversation.model';
import { normalizeForComparison } from '../../../../shared/utils/text-normalization.utils';

@Component({
  selector: 'app-agent-conversations-inbox',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="flex flex-col h-full min-h-0 bg-white dark:bg-gray-900">
      <!-- Header / Filters: shrink-0 para que la cabecera y los filtros sigan visibles
           mientras se recorre la lista (inc66). -->
      <div class="shrink-0 p-4 border-b border-gray-200 dark:border-gray-800">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Bandeja de Agente</h2>

        <!-- Buscador. Filtra en memoria a propósito: GET /api/agent/conversations devuelve la
             bandeja COMPLETA del agente (no pagina), así que lo que hay en pantalla es todo lo
             que hay que buscar y no hace falta ir al backend en cada tecla. -->
        <div class="mb-3">
          <div class="relative">
            <svg class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                 xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="7"></circle>
              <path stroke-linecap="round" d="M20 20l-3.5-3.5"></path>
            </svg>
            <input
              type="text"
              [ngModel]="busqueda"
              (ngModelChange)="buscar($event)"
              placeholder="Buscar nombre, contacto, #id o agente…"
              aria-label="Buscar conversaciones"
              class="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-9 text-xs text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <button *ngIf="busqueda" type="button" (click)="limpiarBusqueda()"
              aria-label="Limpiar búsqueda"
              class="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200">
              <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18"></path>
              </svg>
            </button>
          </div>
          <!-- Cuántas quedan de cuántas: sin esto una búsqueda sin resultados se confunde con
               un filtro de estado vacío. -->
          <p *ngIf="busqueda" class="mt-1 text-[10px] text-gray-400">
            {{ conversacionesVisibles.length }} de {{ conversations.length }}
          </p>
        </div>

        <div class="flex gap-2 text-sm flex-wrap">
          <button (click)="setFilter('ALL')"
            class="px-3 py-1 rounded-full font-medium transition text-xs"
            [ngClass]="currentFilter === 'ALL' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'">
            Todos
          </button>
          <button (click)="setFilter('WAITING_AGENT')"
            class="px-3 py-1 rounded-full font-medium transition text-xs"
            [ngClass]="currentFilter === 'WAITING_AGENT' ? 'bg-yellow-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'">
            En Cola
          </button>
          <button (click)="setFilter('LIVE_CHAT')"
            class="px-3 py-1 rounded-full font-medium transition text-xs"
            [ngClass]="currentFilter === 'LIVE_CHAT' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'">
            Activos
          </button>
          <button (click)="setFilter('CLOSED')"
            class="px-3 py-1 rounded-full font-medium transition text-xs"
            [ngClass]="currentFilter === 'CLOSED' ? 'bg-gray-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'">
            Cerrados
          </button>
        </div>

        <!-- inc72: sin esto todos los agentes veían exactamente la misma bandeja y no había
             forma de quedarse solo con las propias. El backend ya aceptaba assignedToMe. -->
        <div class="mt-3 flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          <button (click)="setAlcance('ALL')" type="button"
            class="flex-1 rounded-md px-3 py-1 text-xs font-medium transition"
            [ngClass]="alcance === 'ALL' ? 'bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'">
            Todas
          </button>
          <button (click)="setAlcance('MINE')" type="button"
            class="flex-1 rounded-md px-3 py-1 text-xs font-medium transition"
            [ngClass]="alcance === 'MINE' ? 'bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'">
            Asignadas a mí
          </button>
        </div>
      </div>

      <!-- List: scroll vertical propio, independiente del de la conversación -->
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div *ngIf="loading && conversations.length === 0" class="p-8 flex justify-center">
          <svg class="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        </div>
        
        <div *ngIf="error" class="p-4 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          {{ error }}
        </div>

        <ng-container *ngIf="!loading || conversations.length > 0">
          <div *ngIf="conversations.length === 0 && !loading" class="p-6 text-center text-gray-400 text-sm">
            No hay conversaciones para este filtro.
          </div>

          <!-- Distinto del vacío de arriba: aquí SÍ hay conversaciones en el filtro, lo que no
               encaja es lo buscado. Con el mismo texto para los dos casos parecía que el filtro
               se había quedado sin nada. -->
          <div *ngIf="conversations.length > 0 && conversacionesVisibles.length === 0"
               class="p-6 text-center text-sm text-gray-400">
            Ninguna conversación coincide con «{{ busqueda }}».
            <button type="button" (click)="limpiarBusqueda()"
                    class="mt-1 block w-full font-medium text-blue-500 hover:underline">
              Limpiar búsqueda
            </button>
          </div>

          <div *ngFor="let conv of conversacionesVisibles"
               (click)="seleccionar(conv.conversationId)"
               class="p-4 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition relative"
               [ngClass]="{
                 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500': selectedId === conv.conversationId,
                 'hover:bg-gray-50 dark:hover:bg-gray-800/50': selectedId !== conv.conversationId
               }">

            <!-- El número va SIEMPRE al lado: con varios "Visitante Anónimo" en la lista era
                 imposible saber cuál es cuál, y es el dato que identifica la conversación en
                 tickets y enlaces. El título que le ponga el agente manda sobre el nombre. -->
            <div class="flex justify-between items-start mb-1">
              <span class="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate pr-2">
                {{ conv.agentTitle || conv.visitorName || 'Visitante Anónimo' }}
                <span class="text-[10px] font-normal text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded ml-1">
                  #{{ conv.conversationId }}
                </span>
              </span>
              <span class="text-[10px] text-gray-400 shrink-0">{{ conv.lastMessageAt | date:'shortTime' }}</span>
            </div>

            <!-- Con título puesto, el visitante pasa a la segunda línea: el agente sigue
                 necesitando saber con quién habla. -->
            <p *ngIf="conv.agentTitle" class="text-[11px] text-gray-500 dark:text-gray-400 truncate mb-1">
              {{ conv.visitorName || 'Visitante Anónimo' }}
            </p>

            <!-- La ficha del CRM, cuando dice algo que la fila no dice ya. En un chat anónimo
                 vinculado a un contacto es el único dato que identifica a la persona. -->
            <p *ngIf="contactoAparte(conv) as contacto"
               class="mb-1 truncate text-[11px] text-gray-600 dark:text-gray-300">
              <span class="text-gray-400 dark:text-gray-500">Contacto:</span> {{ contacto }}
            </p>

            <p class="text-xs text-gray-500 dark:text-gray-400 truncate mb-2">
              <!-- inc47: el nombre del chatflow, nunca su id interno. El fallback solo entra si
                   el backend no lo resolvió (chatflow borrado). -->
              <span *ngIf="conv.chatflowId" class="text-blue-500">{{ conv.chatflowName || ('Chatflow #' + conv.chatflowId) }}</span>
              <span *ngIf="!conv.chatflowId">Sin mensajes aún...</span>
            </p>

            <!-- Quién la lleva: diferenciar las propias del resto era la mitad del inc72. -->
            <p class="text-[11px] text-gray-400 truncate">
              {{ conv.assignedAgentId ? (conv.assignedAgentName || ('Agente #' + conv.assignedAgentId)) : 'Sin asignar' }}
            </p>

            <div class="flex items-center justify-between mt-2">
              <div class="flex items-center gap-2">
                <span class="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      [ngClass]="{
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400': conv.status === 'WAITING_AGENT',
                        'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400': conv.status === 'LIVE_CHAT',
                        'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400': conv.status === 'CLOSED'
                      }">
                  {{ estadoLegible(conv.status) }}
                </span>
                <span *ngIf="conv.escalatedAt" class="text-[10px] text-red-500 font-medium ml-1">Escalado</span>
              </div>
              
              <span *ngIf="conv.unreadCount > 0"
                    [title]="conv.unreadCount === 1 ? '1 mensaje del visitante sin responder' : conv.unreadCount + ' mensajes del visitante sin responder'"
                    class="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {{ conv.unreadCount }}
              </span>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  `,
  // El elemento anfitrión de un componente Angular es `display: block` por defecto: como hijo
  // de la columna flex NO se dejaba encoger (min-height: auto), así que crecía hasta el alto
  // del contenido —1297 px dentro de una columna de 756— y el `h-full` de dentro se resolvía
  // contra esa altura inflada. Resultado: la lista quedaba recortada por el overflow del
  // contenedor y SIN scroll propio, justo el síntoma del incidente (inc66). Las clases del
  // hijo no pueden arreglarlo: el que tiene que ceder es el anfitrión.
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
  `]
})
export class AgentConversationsInboxComponent implements OnInit, OnDestroy {
  @Input() selectedId: number | null = null;
  @Output() conversationSelected = new EventEmitter<number>();

  conversations: AgentConversationListItem[] = [];
  /** Lo que se pinta: `conversations` pasado por el buscador. */
  conversacionesVisibles: AgentConversationListItem[] = [];
  /** Texto del buscador. Sobrevive a los ticks del polling y a cambiar de filtro. */
  busqueda = '';
  loading = true;
  error = '';
  currentFilter: 'ALL' | 'WAITING_AGENT' | 'LIVE_CHAT' | 'CLOSED' = 'ALL';
  /** inc72: `MINE` manda `assignedToMe=true`, que el backend ya resolvía contra el JWT. */
  alcance: 'ALL' | 'MINE' = 'ALL';

  /** La elección sobrevive a recargar la bandeja: es una preferencia de trabajo, no un filtro puntual. */
  private static readonly SCOPE_STORAGE_KEY = 'agent-inbox-scope';

  private destroy$ = new Subject<void>();
  private readonly POLLING_INTERVAL = 15000;

  constructor(private api: AgentConversationApiService) {}

  ngOnInit() {
    const guardado = localStorage.getItem(AgentConversationsInboxComponent.SCOPE_STORAGE_KEY);
    if (guardado === 'MINE' || guardado === 'ALL') {
      this.alcance = guardado;
    }
    this.iniciarPolling();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFilter(filter: 'ALL' | 'WAITING_AGENT' | 'LIVE_CHAT' | 'CLOSED') {
    this.currentFilter = filter;
    this.cargarConversaciones();
  }

  buscar(texto: string) {
    this.busqueda = texto;
    this.aplicarBusqueda();
  }

  limpiarBusqueda() {
    if (!this.busqueda) return;
    this.busqueda = '';
    this.aplicarBusqueda();
  }

  /**
   * Recalcula la lista visible. Se hace aquí y no con un getter en la plantilla porque un
   * getter que filtra se ejecuta en CADA ciclo de detección de cambios, y esta lista se
   * repinta cada 15 s por el polling.
   */
  private aplicarBusqueda() {
    const termino = normalizeForComparison(this.busqueda).replace(/#/g, '');
    this.conversacionesVisibles = termino
      ? this.conversations.filter(conv => this.textoBuscable(conv).includes(termino))
      : this.conversations;
  }

  /**
   * Nombre de la ficha del CRM cuando aporta algo que la fila no dice ya.
   *
   * Si el contacto se llama igual que el título o que el visitante, repetirlo solo gastaría
   * una línea; lo que hace falta es el caso contrario: un «Visitante Anónimo» cuya
   * conversación SÍ está vinculada a una ficha, donde el nombre del contacto es lo único
   * que dice con quién se está hablando.
   */
  contactoAparte(conv: AgentConversationListItem): string | null {
    const contacto = conv.contactName?.trim();
    if (!contacto) return null;
    const contactoNormalizado = normalizeForComparison(contacto);
    const yaVisible = [conv.agentTitle, conv.visitorName]
      .filter((valor): valor is string => !!valor)
      .some((valor) => normalizeForComparison(valor) === contactoNormalizado);
    return yaVisible ? null : contacto;
  }

  /**
   * Todo lo que la fila enseña, en un solo texto: el título que le puso el agente, el
   * visitante, la ficha del CRM, quién la lleva, el chatflow de origen y el #id que se usa en
   * tickets y enlaces. Buscar solo por el nombre del visitante no serviría de nada en una
   * bandeja llena de «Visitante Anónimo», que es justo cuando hace falta buscar.
   *
   * Los textos de relleno de la fila («Visitante Anónimo», «Sin asignar», «Agente #7»,
   * «Chatflow #14») entran igual que los datos reales, y no es un detalle: ninguno de ellos
   * existe en el backend — los pinta esta plantilla cuando el campo llega null. Sin esto,
   * buscar lo que se está leyendo en pantalla devolvía cero resultados.
   */
  private textoBuscable(conv: AgentConversationListItem): string {
    const partes = [
      conv.agentTitle,
      conv.contactName,
      conv.visitorName || 'Visitante Anónimo',
      conv.assignedAgentId
        ? (conv.assignedAgentName || 'Agente #' + conv.assignedAgentId)
        : 'Sin asignar',
      conv.chatflowId ? (conv.chatflowName || 'Chatflow #' + conv.chatflowId) : '',
      String(conv.conversationId)
    ].filter((parte): parte is string => !!parte);
    return normalizeForComparison(partes.join(' ')).replace(/#/g, '');
  }

  setAlcance(alcance: 'ALL' | 'MINE') {
    if (this.alcance === alcance) return;
    this.alcance = alcance;
    localStorage.setItem(AgentConversationsInboxComponent.SCOPE_STORAGE_KEY, alcance);
    this.cargarConversaciones();
  }

  /** Filtros vigentes. El polling los relee en cada tick, así que respeta el último elegido. */
  private filtrosActuales(): AgentConversationFilter {
    const filtros: AgentConversationFilter = {};
    if (this.currentFilter !== 'ALL') {
      filtros.status = this.currentFilter;
    }
    if (this.alcance === 'MINE') {
      filtros.assignedToMe = true;
    }
    return filtros;
  }

  private iniciarPolling() {
    timer(0, this.POLLING_INTERVAL)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.api.getConversations(this.filtrosActuales()))
      )
      .subscribe({
        next: (data) => {
          this.conversations = data;
          this.aplicarBusqueda();
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Error al cargar conversaciones.';
          this.loading = false;
          console.error(err);
        }
      });
  }

  private cargarConversaciones() {
    this.api.getConversations(this.filtrosActuales()).subscribe({
      next: (data) => { this.conversations = data; this.aplicarBusqueda(); this.loading = false; },
      error: (err) => console.error(err)
    });
  }

  /** Estado en el idioma de la interfaz; el diccionario es el mismo que usa el detalle. */
  estadoLegible(status?: string | null): string {
    return conversationStatusLabel(status);
  }

  seleccionar(conversationId: number) {
    if (!conversationId) {
      console.error('Conversación sin conversationId');
      return;
    }
    this.conversationSelected.emit(conversationId);
  }
}
